import { useRef } from 'react';
import { calmTokens as t } from '../../shared/tokens';
import { IconX } from '../../shared/icons';
import CalmModal from '../shared/CalmModal';

function TypeButton({ active, color, label, onClick }) {
  const bg = active
    ? color === 'debit'
      ? t.debitDim
      : t.creditDim
    : 'transparent';
  const border = active
    ? color === 'debit'
      ? t.debitBorder
      : t.creditBorder
    : t.border;
  const fg = active ? (color === 'debit' ? t.debit : t.credit) : t.textDim;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        flex: 1,
        padding: 'var(--modal-type-pad)',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 10,
        color: fg,
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: -0.1,
        cursor: 'pointer',
        fontFamily: t.fontUi,
        transition: 'all .15s',
        touchAction: 'manipulation',
      }}
    >
      {label}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--modal-field-gap)',
        fontSize: 'var(--modal-label)',
        color: t.textDim,
        fontFamily: t.fontUi,
        letterSpacing: 0.1,
      }}
    >
      {label}
      {children}
    </label>
  );
}

const inputStyle = {
  background: 'rgba(0,0,0,0.2)',
  border: `1px solid ${t.border}`,
  borderRadius: 10,
  padding: 'var(--modal-input-pad)',
  color: t.text,
  fontSize: 14,
  fontFamily: t.fontUi,
  outline: 'none',
  transition: 'border-color .15s',
  width: '100%',
  boxSizing: 'border-box',
};

export default function CalmTransactionModal({
  open,
  mode = 'add',
  data,
  onChange,
  onSubmit,
  onClose,
  loading = false,
}) {
  const descriptionRef = useRef(null);

  const header = (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        padding: 'var(--modal-head-pad)',
        borderBottom: `1px solid ${t.border}`,
      }}
    >
      <div>
        <h2
          id="calm-tx-title"
          style={{
            fontFamily: t.fontDisp,
            fontSize: 'var(--modal-title)',
            fontWeight: 500,
            letterSpacing: -0.4,
            color: t.text,
          }}
        >
          {mode === 'edit' ? 'Modifier l’opération' : 'Nouvelle opération'}
        </h2>
        <div
          className="calm-modal-subtitle"
          style={{
            fontSize: 12,
            color: t.textMute,
            marginTop: 4,
            fontFamily: t.fontUi,
          }}
        >
          {mode === 'edit'
            ? 'Ajustez les informations de la transaction.'
            : 'Décrivez la transaction à enregistrer.'}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        title="Fermer"
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'transparent',
          border: `1px solid ${t.border}`,
          color: t.textMute,
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
          padding: 0,
          flexShrink: 0,
          touchAction: 'manipulation',
        }}
      >
        <IconX size={14} />
      </button>
    </div>
  );

  const footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        padding: 'var(--modal-foot-pad)',
        borderTop: `1px solid ${t.border}`,
        background: t.surface,
        borderRadius: '0 0 18px 18px',
      }}
    >
      <button
        type="button"
        onClick={onClose}
        style={{
          padding: 'var(--modal-btn-pad)',
          background: 'transparent',
          border: `1px solid ${t.border}`,
          color: t.textDim,
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
          fontFamily: t.fontUi,
          letterSpacing: -0.1,
          touchAction: 'manipulation',
        }}
      >
        Annuler
      </button>
      <button
        type="submit"
        disabled={loading}
        style={{
          padding: 'var(--modal-btn-pad-primary)',
          background: t.text,
          border: 'none',
          color: t.bg,
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 500,
          cursor: loading ? 'progress' : 'pointer',
          fontFamily: t.fontUi,
          letterSpacing: -0.1,
          opacity: loading ? 0.7 : 1,
          touchAction: 'manipulation',
        }}
      >
        {loading
          ? 'En cours…'
          : mode === 'edit'
            ? 'Enregistrer'
            : 'Ajouter'}
      </button>
    </div>
  );

  return (
    <CalmModal
      open={open}
      as="form"
      onSubmit={onSubmit}
      onClose={onClose}
      labelledBy="calm-tx-title"
      header={header}
      footer={footer}
      zIndex={60}
      dismissible={!loading}
      // En création on ouvre directement sur la description ; en modification
      // on laisse le focus sur la carte pour ne pas déployer le clavier.
      initialFocusRef={mode === 'add' ? descriptionRef : null}
      maxWidth="var(--modal-max-w)"
      bodyStyle={{ padding: 'var(--modal-body-pad)' }}
    >
      {/* En paysage, ce conteneur passe en grille 3 colonnes — date, montant et
          type sur une ligne, description sur la suivante (voir
          `.calm-tx-fields` dans index.css) : le formulaire tient alors
          entièrement à l'écran, sans défilement. */}
      <div
        className="calm-tx-fields"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--modal-body-gap)',
        }}
      >
      <Field label="Date">
        <input
          type="date"
          value={data.date}
          onChange={(e) => onChange({ ...data, date: e.target.value })}
          required
          style={inputStyle}
        />
      </Field>

      <Field label="Description">
        <input
          ref={descriptionRef}
          type="text"
          value={data.description}
          onChange={(e) =>
            onChange({ ...data, description: e.target.value })
          }
          placeholder="Ex. Courses Carrefour"
          required
          autoComplete="off"
          enterKeyHint="next"
          style={inputStyle}
        />
      </Field>

      <Field label="Type">
        <div style={{ display: 'flex', gap: 8, minWidth: 0 }}>
          <TypeButton
            active={data.type === 'debit'}
            color="debit"
            label="Débit"
            onClick={() => onChange({ ...data, type: 'debit' })}
          />
          <TypeButton
            active={data.type === 'credit'}
            color="credit"
            label="Crédit"
            onClick={() => onChange({ ...data, type: 'credit' })}
          />
        </div>
      </Field>

      <Field label="Montant (€)">
        <input
          type="number"
          step="0.01"
          min="0.01"
          value={data.amount}
          onChange={(e) => onChange({ ...data, amount: e.target.value })}
          placeholder="0,00"
          required
          inputMode="decimal"
          enterKeyHint="done"
          style={{
            ...inputStyle,
            fontFamily: t.fontMono,
            fontVariantNumeric: 'tabular-nums',
          }}
        />
      </Field>
      </div>
    </CalmModal>
  );
}
